<template>
  <q-dialog v-bind="$attrs" v-model="showModal">
    <div :class="['flex w-full gap-4 rounded-lg bg-white p-4 shadow-lg ', classes]">
      <header
        v-if="isShowHeader"
        class="w-full text-base leading-6 pb-2 flex items-center justify-between"
        :class="{ '!justify-end': isBlank(title) }">
        <slot name="header">
          <span v-if="title">{{ title }}</span>
        </slot>
        <span>&nbsp;</span>
        <q-btn
          v-close-popup
          icon="close"
          dense
          flat
          class="!bg-transparent hover:!bg-transparent !text-gray-600 !text-sm" />
      </header>
      <div class="flex pb-2 w-full justify-center items-center">
        <slot name="body" />
      </div>
      <div v-if="isShowFooter" class="flex w-full items-center justify-center border-b-0">
        <slot name="footer">
          <div v-if="type === 'success'">
            <button type="button" class="button-success bg-primary" @click="submit">{{ submitTextValue }}</button>
          </div>
          <div v-if="type === 'error'">
            <button type="button" class="btn-cancel text-gray-500" @click="cancel">{{ cancelTextValue }}</button>
          </div>
          <div v-if="type === 'confirm'" class="flex items-center justify-end gap-4">
            <button type="button" class="btn-cancel text-gray-500" @click="cancel">{{ cancelTextValue }}</button>
            <button type="button" class="button-success" :class="submitColor" @click="submit">
              {{ submitTextValue }}
            </button>
          </div>
        </slot>
      </div>
    </div>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isBlank } from '@/utils/lang';

const { t } = useI18n();

interface Props {
  modelValue: boolean;
  isShowHeader?: boolean;
  isShowFooter?: boolean;
  type?: string;
  title?: string;
  submitText?: string;
  submitColor?: string;
  cancelText?: string;
  classes?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  isShowHeader: true,
  isShowFooter: true,
  type: 'success',
  title: '',
  submitText: '',
  cancelText: '',
  submitColor: 'bg-primary',
  classes: () => ['!max-w-md'],
});

const emit = defineEmits(['update:modelValue', 'submit', 'cancel']);

// Computed properties for default text values with i18n
const submitTextValue = computed(() => props.submitText || t('modal.submit'));
const cancelTextValue = computed(() => props.cancelText || t('modal.cancel'));

const showModal = computed({
  get: () => props.modelValue,
  set: (newValue) => {
    emit('update:modelValue', newValue);
  },
});
const cancel = () => {
  emit('cancel');
};
const submit = () => {
  emit('submit');
};
</script>

<style lang="postcss" scoped>
@reference "tailwindcss";

.button-success,
.btn-cancel {
  @apply transform transition-transform duration-300 hover:translate-y-1;
  height: 40px;
  width: 140px;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
}
.btn-cancel {
  @apply border border-gray-300 bg-white;
}
.button-success {
  @apply border-none bg-white text-white;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.16);
}
</style>
